import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Calendar, Clock } from 'lucide-react';
import { POSTS } from '../data/site';

const Meta = ({ post }) => (
  <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] tracking-[0.18em] text-ink/40 font-medium">
    <span className="text-terra">{post.category.toUpperCase()}</span>
    <span className="flex items-center gap-1.5"><Calendar size={11} /> {post.date.toUpperCase()}</span>
    <span className="flex items-center gap-1.5"><Clock size={11} /> {post.read.toUpperCase()} READ</span>
  </p>
);

export default function Blog() {
  const [featured, ...rest] = POSTS;
  return (
    <main data-testid="blog-page" className="pt-40 pb-32 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <p className="text-[10px] tracking-[0.3em] text-terra font-medium mb-6">BLOG & NEWS — GUIDES FROM THE GROUND</p>
        <motion.h1
          initial={{ opacity: 0, y: 34 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-heading font-extrabold tracking-tighter leading-[0.95] text-5xl sm:text-7xl text-ink mb-16"
          data-testid="blog-headline"
        >
          Stories &<br />Guides
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-16"
          data-testid={`blog-featured-${featured.slug}`}
        >
          <Link to={`/blog/${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-2 border border-clay">
            <div className="overflow-hidden aspect-[16/10] bg-stone">
              <img src={featured.image} alt={featured.title} loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
            </div>
            <div className="bg-stone p-8 lg:p-12 flex flex-col justify-center">
              <Meta post={featured} />
              <h2 className="mt-5 font-heading font-extrabold tracking-tighter text-2xl sm:text-3xl lg:text-4xl text-ink group-hover:text-terra transition-colors duration-300 leading-tight">
                {featured.title}
              </h2>
              <p className="mt-4 text-sm lg:text-base text-ink/60 leading-relaxed">{featured.excerpt}</p>
              <span className="mt-7 text-[11px] tracking-[0.2em] font-medium text-ink flex items-center gap-2 group-hover:text-terra transition-colors duration-300">
                READ ARTICLE <ArrowUpRight size={14} />
              </span>
            </div>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((post, i) => (
            <motion.div
              key={post.slug}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }} transition={{ duration: 0.7, delay: (i % 3) * 0.08 }}
              data-testid={`blog-card-${post.slug}`}
            >
              <Link to={`/blog/${post.slug}`} className="group block">
                <div className="overflow-hidden aspect-[16/10] bg-stone">
                  <img src={post.image} alt={post.title} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                </div>
                <div className="pt-5">
                  <Meta post={post} />
                  <h3 className="mt-3 font-heading font-bold text-lg tracking-tight text-ink group-hover:text-terra transition-colors duration-300 leading-snug">
                    {post.title}
                  </h3>
                  <p className="mt-2 text-sm text-ink/55 leading-relaxed line-clamp-2">{post.excerpt}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
}
